import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { registerConfirmOpener } from "../../utils/dialog";

// Single global in-app dialog. Rendered via a portal to <body> so it always
// escapes any transformed/overflow-clipped container. Replaces window.confirm
// and window.prompt everywhere (no native "localhost says" popups).
export default function ConfirmDialogHost() {
  const [cfg, setCfg] = useState(null);
  const [value, setValue] = useState("");
  const resolverRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    registerConfirmOpener((c) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setValue(c.type === "prompt" ? c.defaultValue || "" : "");
        setCfg(c);
      })
    );
    return () => registerConfirmOpener(null);
  }, []);

  const settle = useCallback((result) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setCfg(null);
    if (r) r(result);
  }, []);

  const onConfirm = useCallback(() => {
    settle(cfg && cfg.type === "prompt" ? (value ?? "") : true);
  }, [cfg, value, settle]);

  const onCancel = useCallback(() => {
    settle(cfg && cfg.type === "prompt" ? null : false);
  }, [cfg, settle]);

  useEffect(() => {
    if (!cfg) return;
    if (cfg.type === "prompt") setTimeout(() => inputRef.current?.focus(), 30);
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      else if (e.key === "Enter" && cfg.type !== "prompt") { e.preventDefault(); onConfirm(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cfg, onConfirm, onCancel]);

  if (!cfg) return null;

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div role="dialog" aria-modal="true" style={{ background: "var(--n-0)", borderRadius: 14, width: "min(440px,100%)", boxShadow: "0 24px 60px rgba(2,6,23,.35)", overflow: "hidden" }}>
        <div style={{ padding: "20px 22px 6px" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: cfg.danger ? "var(--danger-600)" : "var(--n-900)" }}>{cfg.title}</h3>
          {cfg.message && (
            <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--n-600)", lineHeight: 1.55, whiteSpace: "pre-line" }}>{cfg.message}</p>
          )}
        </div>
        {cfg.type === "prompt" && (
          <div style={{ padding: "12px 22px 0" }}>
            <input
              ref={inputRef}
              type={cfg.inputType || "text"}
              value={value}
              placeholder={cfg.placeholder || ""}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onConfirm(); } }}
              style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--n-300)", borderRadius: 9, fontSize: 14, outline: "none", boxSizing: "border-box" }}
            />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "18px 22px 20px" }}>
          <button onClick={onCancel} style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid var(--n-200)", background: "var(--n-0)", color: "var(--n-600)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{cfg.cancelText}</button>
          <button onClick={onConfirm} style={{ padding: "9px 18px", borderRadius: 9, border: "none", background: cfg.danger ? "var(--danger-500)" : "var(--clr-accent)", color: "var(--n-0)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{cfg.confirmText}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
